import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { X } from "lucide-react"; // Import Lucide React icon
import { GoogleLogo, LoginImg2, logoLight } from "../../assets/svg/Index";
import { Button, InputField } from "@/components";
import { useApi } from "@/";
import { useFieldError, useValidation } from "@/";
import { LoginSchema } from "../../schemas";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import RecoverPassword from "./RecoverPassword";
import VerifyEmailDialog from "./VerifyEmailDialog";
import { useGoogleLogin } from "@react-oauth/google";

const LoginPage = () => {
  const [formData, setFormData] = useState({ email: "", password: "" });
  const navigate = useNavigate();
  const [generalError, setGeneralError] = useState("");
  const { callApi, loading } = useApi();
  const { validate } = useValidation(LoginSchema);
  const [formError, setFormError] = useState([]);
  const getErrorMessage = useFieldError(formError);
  const [alertOpen, setAlertOpen] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  // Handle input changes
  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setGeneralError("");

    // Validate the form data
    const result = validate(formData);
    console.log("Validation result:", result);
    if (!result.isValid) {
      setFormError(result.errors);
      return;
    }

    try {
      // Call the API to register the user
      const response = await callApi("user/login", "POST", result.data);

      // Check if the registration was successful
      if (response.success) {
        const now = new Date();
        const expiryDate = new Date(now.getTime() + 24 * 60 * 60 * 1000);

        const token = response.data.token;
        const refreshToken = response.data.refreshToken;
        localStorage.setItem("token", token);
        localStorage.setItem("refreshToken", refreshToken);
        navigate("/"); // Redirect to dashboard on success
      } else {
        // Display API-specific error message
        console.log("Login failed:", response);
        setFormError([]);
        setGeneralError(response?.error || "Login failed. Please try again.");
        if (response.errorObj?.code === "EMAIL_NOT_VERIFIED") {
          console.log("EMAIL_NOT_VERIFIED");
          setShowDialog(true);
        }
      }
    } catch (error) {
      // Handle unexpected errors (e.g., network issues)
      console.error("Unexpected error during login:", error);
      setGeneralError(
        error.message || "Something went wrong. Please try again."
      );
    }
  };

  // Improved Google login implementation
  const handleGoogleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        setGoogleLoading(true);
        setGeneralError("");

        // Call your backend API with the Google authorization code
        const result = await callApi("user/auth/google", "POST", {
          code: tokenResponse.code,
        });

        if (result.success) {
          const token = result.data.token;
          const refreshToken = result.data.refreshToken;
          localStorage.setItem("token", token);
          localStorage.setItem("refreshToken", refreshToken);
          navigate("/");
        } else {
          setGeneralError(
            result?.error || "Google login failed. Please try again."
          );
        }
      } catch (error) {
        console.error("Google login error:", error);
        setGeneralError("Failed to login with Google. Please try again.");
      } finally {
        setGoogleLoading(false);
      }
    },
    // Change from implicit flow to authorization code flow
    flow: "auth-code",
    onError: (error) => {
      console.error("Google login error:", error);
      setGeneralError(
        "Google login failed: " +
          (error?.error_description || error?.message || "Unknown error")
      );
      setGoogleLoading(false);
    },
    onNonOAuthError: (error) => {
      console.error("Non-OAuth error:", error);
      setGeneralError(
        "Google login configuration error: " +
          (error?.error_description ||
            error?.message ||
            "redirect_uri_mismatch")
      );
      setGoogleLoading(false);
    },
  });

  // Google button click handler
  const initiateGoogleLogin = () => {
    try {
      setGoogleLoading(true);
      handleGoogleLogin();
    } catch (error) {
      console.error("Failed to initiate Google login:", error);
      setGeneralError(
        "Failed to start Google authentication. Please try again."
      );
      setGoogleLoading(false);
    }
  };

  const handleClose = () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setGeneralError("You are not logged in, please log-in first.");
      return;
    }
    navigate("/");
  };

  return (
    <div className="relative flex w-full h-dvh">
      {/* Close Button */}
      <button
        className="absolute top-4 right-4 p-2 z-10 rounded-xl bg-background_2/60 hover:bg-primary/30 transition-all"
        onClick={handleClose}
      >
        <X className="w-5 h-5 text-secondary/60" />
      </button>

      {/* Left Side */}
      <div className="hidden sm:flex w-1/2 flex-col justify-center items-start bg-background_2 p-2 relative">
        <img
          src={LoginImg2}
          alt="Login Visual"
          className="w-full h-full rounded-xl object-cover"
        />
      </div>

      {/* Right Side */}
      <div className="w-full h-full sm:w-1/2 flex flex-col justify-center bg-background gap-3 p-8 relative">
        <a href="https://indiancouriercompany.com">
          <img
            src={logoLight}
            alt="Logo"
            className="absolute top-4 left-7 w-40 opacity-90"
          />
        </a>
        <div className="w-full max-w-lg mb-8">
          <h2 className="text-headline3 text-secondary font-nunito font-semibold">
            Welcome Back,
          </h2>
          <p className="text-subtitle2 text-secondary/65 font-ptsans w-[70%]">
            Shipping Without Stress, From Your Place to Your Customer's.
          </p>
        </div>
        <form
          className="w-full flex flex-col gap-2 max-w-lg"
          onSubmit={handleSubmit}
        >
          {/* General Error Message */}
          {generalError && <p className="text-red-500 mb-4">{generalError}</p>}

          {/* Email Field */}
          <InputField
            type="email"
            id="email"
            value={formData.email}
            onChange={(value) => handleChange("email", value)}
            placeholder="Enter your email"
            error={getErrorMessage("email")}
            disabled={loading}
          />

          {/* Password Field */}
          <InputField
            type="password"
            id="password"
            value={formData.password}
            error={getErrorMessage("password")}
            onChange={(value) => handleChange("password", value)}
            placeholder="Enter your password"
            disabled={loading}
          />
          <div className="flex items-center justify-end w-full max-w-lg !-mt-1.5 ">
            <AlertDialog open={alertOpen} onOpenChange={setAlertOpen}>
              <AlertDialogTrigger asChild>
                <span className="text-primary/80 hover:underline text-body2 mr-1">
                  Forgot Password?
                </span>
              </AlertDialogTrigger>
              <AlertDialogContent className="!bg-background !rounded-xl !w-full !h-fit ">
                <button
                  onClick={() => setAlertOpen(false)}
                  className="flex absolute right-4 top-4 justify-center !h-6 !w-6 !grow-0 !shrink-0 items-center rounded-md bg-background_2/60 hover:bg-primary/30 transition-all"
                >
                  <X className="!h-3 text-secondary/60" />
                </button>
                <AlertDialogHeader>
                  <AlertDialogTitle className="font-nunito !text-secondary/90 !font-bold !text-headline4 md:!!text-headline3 !ml-2 flex justify-between items-center">
                    Recover Your Account
                  </AlertDialogTitle>

                  <AlertDialogDescription className="!w-full flex flex-col"></AlertDialogDescription>
                </AlertDialogHeader>
                <RecoverPassword setAlertOpen={setAlertOpen} />
              </AlertDialogContent>
            </AlertDialog>
          </div>

          {/* Submit Button */}
          <Button type="submit" className="mt-2" isLoading={loading}>
            Login
          </Button>

          {/* Divider */}
          <span className="flex justify-center items-center my-2 text-subtitle1">
            or
          </span>

          {/* Google Login Button */}
          <Button
            type="button"
            onClick={initiateGoogleLogin}
            className="w-full !bg-transparent !text-secondary/80 font-nunito border-2 border-secondary/10 py-2 rounded-xl flex items-center justify-center hover:bg-secondary/5 hover:scale-[0.96] transition-all"
            isLoading={googleLoading}
            loadingText="Please Wait..."
          >
            {!googleLoading && (
              <img src={GoogleLogo} alt="Google" className="w-7 h-7 mr-2" />
            )}
            Continue with Google
          </Button>
        </form>

        {/* Signup Link */}
        <div className="flex items-center justify-center w-full max-w-lg mt-8">
          <p className="text-secondary/80 text-subtitle1">
            Don't have an account?{" "}
            <Link to="/signup" className="text-primary/80 hover:underline">
              Sign up
            </Link>
          </p>
        </div>
      </div>
      <VerifyEmailDialog
        isOpen={showDialog}
        onClose={() => setShowDialog(false)}
        userEmail={formData.email}
      />
    </div>
  );
};

export default LoginPage;
